import {API_URL, handleErrorResponse} from "../utility/utility";

export async function findItemsWithPagination(page, limit, itemCategoryId = null, itemPropertyValueIds = null, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);

    if (itemCategoryId) {
        params.append('itemCategoryId', itemCategoryId);
    }

    if (itemPropertyValueIds && itemPropertyValueIds.length > 0) {
        itemPropertyValueIds.forEach((itemPropertyValueId) => {
            params.append('itemPropertyValueIds[]', itemPropertyValueId);
        });
    }

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/api/items?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch items');

    return response.json();
}

export async function getItem(itemId) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/api/items/${itemId}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to fetch item');

    return response.json();
}

// properties is a list of {name, value}.
const appendProperties = (formData, properties, prefix) => {
    properties.forEach((property, index) => {
        formData.append(`${prefix}[${index}][name]`, property.name);
        formData.append(`${prefix}[${index}][value]`, property.value);
    });
};

// For requests that carry files. No Content-Type header: the browser sets the
// multipart boundary itself.
const postForm = async (url, formData, failureMessage) => {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(url, {
        method  : 'POST',
        body    : formData,
        headers : {
            'Accept'        : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, failureMessage);
};

// image is a File when the admin picked a new one, otherwise null/undefined.
// A file present switches this request to multipart (nested fields use bracket
// notation); with no file, this keeps sending plain JSON exactly as before.
export async function createItem(itemCategoryId, name, description, bruttoPrice, image, isPublic, order, variants) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    if (image) {
        const formData = new FormData();
        formData.append('itemCategoryId', itemCategoryId);
        formData.append('name', name);
        formData.append('bruttoPrice', bruttoPrice);
        formData.append('isPublic', isPublic ? 1 : 0);
        formData.append('order', order);
        formData.append('image', image);

        if (description) {
            formData.append('description', description);
        }

        variants.forEach((variant, index) => {
            formData.append(`variants[${index}][stockQuantity]`, variant.stockQuantity);
            appendProperties(formData, variant.properties, `variants[${index}][properties]`);
        });

        await postForm(`${API_URL}/api/items`, formData, 'Failed to create item');
        return;
    }

    const response = await fetch(`${API_URL}/api/items`, {
        method  : 'POST',
        body    : JSON.stringify({
            itemCategoryId : itemCategoryId,
            name           : name,
            description    : description,
            bruttoPrice    : bruttoPrice,
            image          : null,
            isPublic       : isPublic,
            order          : order,
            variants       : variants
        }),
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to create item');
}

// PHP does not populate file uploads on a native PATCH request, so an image
// file forces this to POST with a `_method=PATCH` spoof field (Laravel's
// method-spoofing convention) instead of issuing a real HTTP PATCH. With no
// file, this keeps sending a real JSON PATCH exactly as before.
export async function updateItem(itemId, fields, image = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    if (image) {
        const formData = new FormData();
        formData.append('_method', 'PATCH');
        formData.append('image', image);

        Object.entries(fields).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            formData.append(key, key === 'isPublic' ? (value ? 1 : 0) : value);
        });

        await postForm(`${API_URL}/api/items/${itemId}`, formData, 'Failed to update item');
        return;
    }

    const response = await fetch(`${API_URL}/api/items/${itemId}`, {
        method  : 'PATCH',
        body    : JSON.stringify(fields),
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to update item');
}

export async function deleteItem(itemId) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/api/items/${itemId}`, {
        method  : 'DELETE',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to delete item');
}

// images are Files in display order.
export async function createItemVariant(itemId, {stockQuantity, properties, images}) {
    const formData = new FormData();
    formData.append('stockQuantity', stockQuantity);
    appendProperties(formData, properties, 'properties');
    images.forEach((image) => formData.append('images[]', image));

    await postForm(`${API_URL}/api/items/${itemId}/variants`, formData, 'Failed to add item variant');
}

// images is the variant's complete image list in display order: {itemVariantImageId}
// keeps an existing image, {file} uploads a new one, and any image left out is deleted.
// An empty list is sent as images="", because the API rejects a missing field
// rather than reading it as "delete all".
// loadedStockQuantity is the stock the form was opened with. If the admin
// changed the stock and a purchase changed it too, the API saves nothing and
// answers ITEM_VARIANT_STOCK_CHANGED_ERROR_CODE, so sold stock is never put back.
// PHP does not populate file uploads on a native PATCH, hence POST with _method.
export async function updateItemVariant(itemVariantId, {stockQuantity, loadedStockQuantity, properties, images}) {
    const formData = new FormData();
    formData.append('_method', 'PATCH');
    formData.append('stockQuantity', stockQuantity);
    formData.append('loadedStockQuantity', loadedStockQuantity);
    appendProperties(formData, properties, 'properties');

    if (images.length === 0) {
        formData.append('images', '');
    }

    images.forEach((image, index) => {
        if (image.file) {
            formData.append(`images[${index}][file]`, image.file);
        }
        else {
            formData.append(`images[${index}][id]`, image.itemVariantImageId);
        }
    });

    await postForm(`${API_URL}/api/item-variants/${itemVariantId}`, formData, 'Failed to update item variant');
}

// A variant that was bought is archived by the API instead of deleted.
export async function deleteItemVariant(itemVariantId) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/api/item-variants/${itemVariantId}`, {
        method  : 'DELETE',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, "Couldn't remove this variant.");
}
